// apps/web/src/pages/CollectorPage.tsx
//
// Cobros — collector dashboard. Lists payment attempts (bank deposits and,
// soon, QPay transactions) and lets an admin approve or reject each one.
// Approval flips the invoice to paid.
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CheckCircle2, XCircle, FileText, RefreshCw, Loader2, Receipt, Banknote,
  ExternalLink, Calendar, Search, ArrowUpDown, ChevronLeft, ChevronRight, AlertTriangle,
  Undo2, PauseCircle, PlayCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { updateAccountFeatures } from '@/services/accounts';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { PermissionGate } from '@/components/PermissionGate';
import {
  fetchPaymentAttempts,
  getReceiptSignedUrl,
  confirmPayment,
  refundPayment,
  type PendingAttemptRow,
  type AttemptStatusFilter,
} from '@/services/payments';
import { supabase } from '@/services/supabase';
import { usePermissions } from '@/hooks/usePermissions';

const PAGE_SIZE = 20;

function formatCurrency(amount: number, currency = 'GTQ'): string {
  return new Intl.NumberFormat('es-GT', { style: 'currency', currency, minimumFractionDigits: 2 }).format(amount);
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('es-GT', { dateStyle: 'medium', timeStyle: 'short' });
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; color: string }> = {
    pending:  { label: 'Pendiente',  color: 'bg-amber-500/15 text-amber-600 border-amber-500/30' },
    approved: { label: 'Aprobado',   color: 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30' },
    rejected: { label: 'Rechazado',  color: 'bg-rose-500/15 text-rose-600 border-rose-500/30' },
    failed:   { label: 'Fallido',    color: 'bg-zinc-500/15 text-zinc-500 border-zinc-500/30' },
    refunded: { label: 'Reembolsado',color: 'bg-violet-500/15 text-violet-600 border-violet-500/30' },
  };
  const cfg = map[status] ?? { label: status, color: '' };
  return <Badge className={`${cfg.color} border`}>{cfg.label}</Badge>;
}

function MethodBadge({ method }: { method: string }) {
  return method === 'qpay'
    ? <Badge className="bg-violet-500/15 text-violet-600 border border-violet-500/30">QPay</Badge>
    : <Badge className="bg-sky-500/15 text-sky-600 border border-sky-500/30">Depósito</Badge>;
}

const TABS: { value: AttemptStatusFilter; label: string }[] = [
  { value: 'pending',  label: 'Pendientes' },
  { value: 'approved', label: 'Aprobados' },
  { value: 'rejected', label: 'Rechazados' },
  { value: 'all',      label: 'Todos' },
];

interface ConfirmDialogState {
  open: boolean;
  mode: 'approve' | 'reject' | 'refund' | null;
  row: PendingAttemptRow | null;
  reason: string;
  busy: boolean;
}

export function CollectorPage() {
  const { hasRole } = usePermissions();
  const canRefund = hasRole(['admin', 'super_admin']);
  const [tab, setTab] = useState<AttemptStatusFilter>('pending');
  const [rows, setRows] = useState<PendingAttemptRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [methodFilter, setMethodFilter] = useState<'qpay' | 'bank_deposit' | ''>('');
  const [sortBy, setSortBy] = useState<'created_at' | 'amount' | 'reviewed_at'>('created_at');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [loading, setLoading] = useState(true);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewName, setPreviewName] = useState<string>('');
  const [confirmDlg, setConfirmDlg] = useState<ConfirmDialogState>({ open: false, mode: null, row: null, reason: '', busy: false });
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Debounce search input → 300ms
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Reset to first page whenever filters change
  useEffect(() => { setPage(0); }, [tab, debouncedSearch, fromDate, toDate, methodFilter, sortBy, sortDir]);

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMsg(null);
    try {
      const result = await fetchPaymentAttempts({
        status: tab,
        search: debouncedSearch,
        fromDate: fromDate || undefined,
        toDate:   toDate || undefined,
        method:   methodFilter || undefined,
        sortBy,
        sortDir,
        limit:  PAGE_SIZE,
        offset: page * PAGE_SIZE,
      });
      setRows(result.rows);
      setTotalCount(result.count);
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Error cargando cobros');
    } finally {
      setLoading(false);
    }
  }, [tab, debouncedSearch, fromDate, toDate, methodFilter, sortBy, sortDir, page]);

  useEffect(() => { load(); }, [load]);

  // Realtime: refresh when any payment attempt or invoice changes.
  useEffect(() => {
    const ch = supabase
      .channel('collector-feed')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'invoice_payment_attempts' }, () => load())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'invoices' }, () => load())
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [load]);

  async function handleViewReceipt(row: PendingAttemptRow) {
    if (!row.receipt_url) return;
    try {
      const url = await getReceiptSignedUrl(row.receipt_url);
      setPreviewUrl(url);
      setPreviewName(`${row.invoice?.invoice_number ?? 'comprobante'} — ${row.account?.name ?? ''}`);
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'No se pudo abrir el comprobante');
    }
  }

  function openApproveDialog(row: PendingAttemptRow) {
    setConfirmDlg({ open: true, mode: 'approve', row, reason: '', busy: false });
  }
  function openRejectDialog(row: PendingAttemptRow) {
    setConfirmDlg({ open: true, mode: 'reject', row, reason: '', busy: false });
  }
  function openRefundDialog(row: PendingAttemptRow) {
    setConfirmDlg({ open: true, mode: 'refund', row, reason: '', busy: false });
  }
  function closeDialog() {
    setConfirmDlg({ open: false, mode: null, row: null, reason: '', busy: false });
  }

  async function submitDialog() {
    const { mode, row, reason } = confirmDlg;
    if (!row || !mode) return;
    if ((mode === 'reject' || mode === 'refund') && reason.trim().length === 0) return;
    setConfirmDlg((s) => ({ ...s, busy: true }));
    try {
      if (mode === 'refund') {
        await refundPayment({ attemptId: row.id, reason: reason.trim() });
      } else {
        await confirmPayment({
          attemptId: row.id,
          action: mode,
          rejectionReason: mode === 'reject' ? reason.trim() : undefined,
        });
      }
      closeDialog();
      await load();
    } catch (err) {
      setConfirmDlg((s) => ({ ...s, busy: false }));
      setErrorMsg(err instanceof Error ? err.message : 'Error procesando solicitud');
    }
  }

  function toggleSort(field: 'created_at' | 'amount') {
    if (sortBy === field) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortBy(field); setSortDir('desc'); }
  }

  const pendingTotal = useMemo(
    () => (tab === 'pending' ? rows.reduce((s, r) => s + Number(r.amount), 0) : 0),
    [tab, rows],
  );

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  return (
    <PermissionGate require="collections.view" fallback={<div className="p-6 text-muted-foreground">Sin permisos</div>}>
      <div className="container mx-auto p-6 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
              <Banknote className="w-6 h-6 text-emerald-500" />
              Cobros
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Revisa y confirma los depósitos bancarios enviados por los clientes desde la app móvil.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
            Refrescar
          </Button>
        </div>

        {errorMsg && (
          <div className="rounded-md border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-sm text-rose-600 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            <span className="flex-1">{errorMsg}</span>
            <button onClick={() => setErrorMsg(null)} className="text-xs underline">Cerrar</button>
          </div>
        )}

        {/* Tabs */}
        <div className="flex flex-wrap gap-1 bg-muted p-1 rounded-lg w-fit">
          {TABS.map((t) => (
            <button
              key={t.value}
              onClick={() => setTab(t.value)}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                tab === t.value
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Filters bar */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por cuenta, factura, banco o referencia…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8"
            />
          </div>
          <div className="flex items-center gap-2 text-xs">
            <Calendar className="w-3.5 h-3.5 text-muted-foreground" />
            <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="h-9 w-[150px]" />
            <span className="text-muted-foreground">→</span>
            <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="h-9 w-[150px]" />
            {(fromDate || toDate) && (
              <Button size="sm" variant="ghost" onClick={() => { setFromDate(''); setToDate(''); }}>Limpiar</Button>
            )}
          </div>
          {/* Method filter */}
          <div className="flex items-center gap-1 bg-muted p-1 rounded-lg">
            {(['', 'bank_deposit', 'qpay'] as const).map((m) => (
              <button
                key={m}
                onClick={() => setMethodFilter(m)}
                className={`px-3 py-1 text-xs rounded-md transition-colors ${
                  methodFilter === m
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {m === '' ? 'Todos' : m === 'bank_deposit' ? 'Depósito' : 'Electrónico'}
              </button>
            ))}
          </div>
        </div>

        {/* KPI strip — only meaningful on pendientes */}
        {tab === 'pending' && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Card><CardContent className="pt-6">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Pendientes (página)</div>
              <div className="text-3xl font-bold text-foreground mt-1">{rows.length}</div>
            </CardContent></Card>
            <Card><CardContent className="pt-6">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Monto pendiente (página)</div>
              <div className="text-3xl font-bold text-amber-600 mt-1">{formatCurrency(pendingTotal)}</div>
            </CardContent></Card>
            <Card><CardContent className="pt-6">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Total registros</div>
              <div className="text-3xl font-bold text-foreground mt-1">{totalCount}</div>
            </CardContent></Card>
          </div>
        )}

        {/* Sort header */}
        <div className="flex items-center justify-between text-xs text-muted-foreground border-b border-border pb-2">
          <div className="flex items-center gap-3">
            <button onClick={() => toggleSort('created_at')} className="inline-flex items-center gap-1 hover:text-foreground">
              <ArrowUpDown className="w-3 h-3" />
              Fecha {sortBy === 'created_at' && (sortDir === 'asc' ? '↑' : '↓')}
            </button>
            <button onClick={() => toggleSort('amount')} className="inline-flex items-center gap-1 hover:text-foreground">
              <ArrowUpDown className="w-3 h-3" />
              Monto {sortBy === 'amount' && (sortDir === 'asc' ? '↑' : '↓')}
            </button>
          </div>
          <div>{totalCount} {totalCount === 1 ? 'resultado' : 'resultados'}</div>
        </div>

        {/* List */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center gap-2 text-muted-foreground py-10 justify-center">
                <Loader2 className="w-4 h-4 animate-spin" /> Cargando…
              </div>
            ) : rows.length === 0 ? (
              <div className="text-center text-muted-foreground py-12">
                <Receipt className="w-8 h-8 mx-auto mb-2 opacity-50" />
                Sin resultados.
              </div>
            ) : (
              <div className="divide-y divide-border">
                {rows.map((row) => (
                  <div key={row.id} className="p-4 flex flex-col lg:flex-row lg:items-start gap-3 hover:bg-muted/30 transition-colors">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-semibold text-foreground">
                          {row.invoice?.invoice_number ?? 'sin factura'}
                        </span>
                        <StatusBadge status={row.status} />
                        <MethodBadge method={row.method} />
                        {row.requires_invoice && (
                          <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-700 dark:text-amber-300 border border-amber-500/20">
                            Solicita factura · NIT {row.receptor_nit || 'CF'}
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-muted-foreground mt-0.5 truncate">
                        {row.account?.account_number} — {row.account?.name}
                      </div>
                      <div className="text-sm text-foreground mt-1">
                        <strong>{formatCurrency(row.amount, row.currency)}</strong>
                        {row.deposit_bank && <span className="text-muted-foreground"> · {row.deposit_bank}</span>}
                        {row.deposit_reference && <span className="text-muted-foreground"> · Ref {row.deposit_reference}</span>}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1 flex items-center gap-2 flex-wrap">
                        <Calendar className="w-3 h-3" />
                        Enviado {formatDate(row.created_at)} por {row.submitter?.full_name ?? row.submitter?.email ?? '—'}
                        {row.reviewed_at && row.status !== 'pending' && (
                          <span>
                            · Revisado {formatDate(row.reviewed_at)} por{' '}
                            {row.reviewer
                              ? (row.reviewer.full_name ?? row.reviewer.email ?? '—')
                              : 'Sistema'}
                          </span>
                        )}
                      </div>
                      {row.rejection_reason && row.status === 'rejected' && (
                        <div className="text-xs text-rose-600 mt-1">Motivo: {row.rejection_reason}</div>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {/* Suspend/reactivate quick action for accounts with rejected payments */}
                      {row.account?.id && row.status === 'rejected' && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="border-red-300 text-red-600 hover:bg-red-500/10"
                          onClick={async () => {
                            if (confirm('¿Suspender cámaras, armado y patrullas por mora? El monitoreo ARC continuará.')) {
                              await updateAccountFeatures(row.account!.id, {
                                feature_cameras: false, feature_panels: false, feature_patrols: false,
                              });
                            }
                          }}
                        >
                          <PauseCircle className="w-4 h-4 mr-1.5" />
                          Suspender servicios
                        </Button>
                      )}
                      {row.receipt_url && (
                        <Button variant="outline" size="sm" onClick={() => handleViewReceipt(row)}>
                          <FileText className="w-4 h-4 mr-1.5" />
                          Ver comprobante
                        </Button>
                      )}
                      {row.status === 'pending' && (
                        <PermissionGate require="collections.confirm">
                          <Button
                            size="sm"
                            className="bg-emerald-600 hover:bg-emerald-700 text-white"
                            onClick={() => openApproveDialog(row)}
                          >
                            <CheckCircle2 className="w-4 h-4 mr-1.5" />
                            Aprobar
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="border-rose-300 text-rose-600 hover:bg-rose-500/10"
                            onClick={() => openRejectDialog(row)}
                          >
                            <XCircle className="w-4 h-4 mr-1.5" />
                            Rechazar
                          </Button>
                        </PermissionGate>
                      )}
                      {row.status === 'approved'
                        && row.method === 'qpay'
                        && !!row.qpay_transaction_id
                        && canRefund && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="border-violet-300 text-violet-600 hover:bg-violet-500/10"
                          onClick={() => openRefundDialog(row)}
                        >
                          <Undo2 className="w-4 h-4 mr-1.5" />
                          Reembolsar
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pagination */}
        {totalCount > PAGE_SIZE && (
          <div className="flex items-center justify-between text-sm">
            <div className="text-muted-foreground">
              Página {page + 1} de {totalPages}
            </div>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="sm" disabled={page === 0 || loading} onClick={() => setPage((p) => Math.max(0, p - 1))}>
                <ChevronLeft className="w-4 h-4" />
                Anterior
              </Button>
              <Button variant="outline" size="sm" disabled={page + 1 >= totalPages || loading} onClick={() => setPage((p) => p + 1)}>
                Siguiente
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Receipt preview modal */}
        {previewUrl && (
          <div
            className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
            onClick={() => setPreviewUrl(null)}
          >
            <div
              className="bg-background rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-3 border-b border-border">
                <div className="text-sm font-medium truncate">{previewName}</div>
                <div className="flex items-center gap-2">
                  <a href={previewUrl} target="_blank" rel="noreferrer" className="text-xs text-primary inline-flex items-center gap-1">
                    Abrir <ExternalLink className="w-3 h-3" />
                  </a>
                  <Button size="sm" variant="outline" onClick={() => setPreviewUrl(null)}>Cerrar</Button>
                </div>
              </div>
              <div className="flex-1 overflow-auto bg-muted/30 flex items-center justify-center">
                {previewUrl.match(/\.pdf(\?|$)/i) ? (
                  <iframe src={previewUrl} title="Comprobante" className="w-full h-[80vh]" />
                ) : (
                  <img src={previewUrl} alt="Comprobante" className="max-h-[80vh] object-contain" />
                )}
              </div>
            </div>
          </div>
        )}

        {/* Approve / Reject dialog */}
        <Dialog open={confirmDlg.open} onOpenChange={(open) => { if (!open && !confirmDlg.busy) closeDialog(); }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {confirmDlg.mode === 'approve' ? 'Confirmar pago'
                  : confirmDlg.mode === 'refund' ? 'Reembolsar pago QPay'
                  : 'Rechazar comprobante'}
              </DialogTitle>
              <DialogDescription>
                {confirmDlg.row && (
                  <span className="block mt-1">
                    Factura <strong>{confirmDlg.row.invoice?.invoice_number}</strong> —{' '}
                    {formatCurrency(confirmDlg.row.amount, confirmDlg.row.currency)}
                    <br />
                    Cuenta: {confirmDlg.row.account?.account_number} — {confirmDlg.row.account?.name}
                  </span>
                )}
              </DialogDescription>
            </DialogHeader>

            {confirmDlg.mode === 'approve' ? (
              <div className="text-sm text-muted-foreground">
                Al aprobar, la factura quedará marcada como <strong>pagada</strong> y el cliente recibirá
                una notificación.
              </div>
            ) : confirmDlg.mode === 'refund' ? (
              <div className="space-y-2">
                <div className="text-sm text-amber-700 bg-amber-500/10 border border-amber-500/30 rounded-md p-3">
                  <strong>Importante:</strong> esto enviará una orden de reembolso a QPay Pro contra la
                  transacción <code>{confirmDlg.row?.qpay_transaction_id}</code>. La factura quedará
                  anulada y el cliente recibirá una notificación.
                </div>
                <label className="text-sm font-medium">Motivo del reembolso (queda registrado)</label>
                <textarea
                  value={confirmDlg.reason}
                  onChange={(e) => setConfirmDlg((s) => ({ ...s, reason: e.target.value }))}
                  rows={3}
                  placeholder="Ej: cobro duplicado, factura anulada, cliente canceló el servicio"
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>
            ) : (
              <div className="space-y-2">
                <label className="text-sm font-medium">Motivo del rechazo (visible para el cliente)</label>
                <textarea
                  value={confirmDlg.reason}
                  onChange={(e) => setConfirmDlg((s) => ({ ...s, reason: e.target.value }))}
                  rows={3}
                  placeholder="Ej: el monto del depósito no coincide con la factura"
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={closeDialog} disabled={confirmDlg.busy}>Cancelar</Button>
              <Button
                onClick={submitDialog}
                disabled={
                  confirmDlg.busy
                  || ((confirmDlg.mode === 'reject' || confirmDlg.mode === 'refund')
                    && confirmDlg.reason.trim().length === 0)
                }
                className={
                  confirmDlg.mode === 'approve'
                    ? 'bg-emerald-600 hover:bg-emerald-700'
                    : confirmDlg.mode === 'refund'
                      ? 'bg-violet-600 hover:bg-violet-700'
                      : 'bg-rose-600 hover:bg-rose-700'
                }
              >
                {confirmDlg.busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
                {confirmDlg.mode === 'approve' ? 'Aprobar pago'
                  : confirmDlg.mode === 'refund' ? 'Confirmar reembolso'
                  : 'Rechazar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </PermissionGate>
  );
}
